import { useEffect, useState } from "react";
import api, { resolveImageUrl } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Star, User, Car, Loader2, Calendar, MessageSquare } from "lucide-react";

function StarRow({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(rating) ? "fill-[#FACC15] text-[#FACC15]" : "text-[#CBD5E1]"}`} />
      ))}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; }
}

export default function DriverPublicProfileModal({ driverId, open, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !driverId) return;
    let active = true;
    setLoading(true);
    setError(null);
    setProfile(null);
    api.get(`/drivers/${driverId}/public`)
      .then(({ data }) => { if (active) setProfile(data); })
      .catch((e) => { if (active) setError(e.response?.data?.detail || "Profil indisponible"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [driverId, open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="driver-public-profile-modal">
        <DialogHeader>
          <DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Profil du chauffeur</DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Avis et informations publiques
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-[#64748B]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
          </div>
        ) : error ? (
          <p className="text-center py-8 text-[#EF4444] text-sm">{error}</p>
        ) : profile ? (
          <div className="space-y-5">
            {/* Header card */}
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-br from-[#0A2540] to-[#1E3A5F]">
              {profile.profile_photo ? (
                <img src={resolveImageUrl(profile.profile_photo)} alt={profile.name} className="w-20 h-20 rounded-full object-cover border-2 border-[#FACC15]" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center"><User className="w-10 h-10 text-[#FACC15]" /></div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-white truncate" style={{ fontFamily: 'Outfit' }} data-testid="driver-public-name">{profile.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <StarRow rating={profile.rating || 0} />
                  <span className="text-xs text-white/80">{(profile.rating || 0).toFixed(1)} ({profile.total_reviews || 0} avis)</span>
                </div>
                {profile.member_since && (
                  <p className="text-[10px] text-white/60 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Membre depuis {formatDate(profile.member_since)}</p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white border border-[#CBD5E1] rounded-xl p-3" data-testid="driver-public-vehicles-count">
                <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Véhicules</p>
                <p className="text-lg font-bold text-[#0A2540] flex items-center gap-1.5"><Car className="w-4 h-4 text-[#3B82F6]" /> {profile.vehicles_count || 0}</p>
              </div>
              <div className="bg-white border border-[#CBD5E1] rounded-xl p-3" data-testid="driver-public-rides-count">
                <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Trajets</p>
                <p className="text-lg font-bold text-[#0A2540]">{profile.rides_count || 0}</p>
              </div>
            </div>

            {/* Reviews */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-[#0A2540]" />
                <p className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Avis des clients</p>
              </div>
              {(profile.reviews || []).length === 0 ? (
                <div className="text-center py-8 text-[#64748B]">
                  <Star className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Aucun avis pour le moment</p>
                </div>
              ) : (
                <ul className="space-y-3" data-testid="driver-public-reviews">
                  {profile.reviews.map((r, i) => (
                    <li key={r.booking_id ? `${r.booking_id}-${i}` : i} className="bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl p-3" data-testid={`driver-review-${i}`}>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          {r.reviewer_photo ? (
                            <img src={resolveImageUrl(r.reviewer_photo)} alt={r.reviewer_name} className="w-7 h-7 rounded-full object-cover" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-[#0A2540] flex items-center justify-center text-[10px] font-bold text-[#FACC15]">{(r.reviewer_name || "?")[0]}</div>
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-[#0A2540] truncate">{r.reviewer_name || "Client"}</p>
                            <p className="text-[10px] text-[#94A3B8]">{formatDate(r.created_at)}</p>
                          </div>
                        </div>
                        <StarRow rating={r.rating || 0} />
                      </div>
                      {r.comment && (
                        <p className="text-xs text-[#0A2540] leading-relaxed mt-1.5">{r.comment}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
